import React, { memo, useCallback, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet/es/Helmet'
import DashboardLayout from '../../layouts/DashboardLayout/DashboardLayout'
import {
  Avatar,
  Button,
  Empty,
  message,
  Popconfirm,
  Space,
  Spin,
  Tooltip,
} from 'antd'
import { inject, observer } from 'mobx-react'
import authenticationStore from '../../stores/authenticationStore'
import { apiUrl } from '../../config'
import {
  DeleteOutlined,
  EditOutlined,
  MailOutlined,
  OrderedListOutlined,
  PhoneOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  UserAddOutlined,
} from '@ant-design/icons'
import CreateDepartmentModal from './CreateDepartmentModal'
import { find } from 'lodash-es'
import { MODAL_MODE } from '../../ui_constants'
import AddUserForm from '../../components/AddUserForm/AddUserForm'
import positionStore from '../../stores/positionStore'
import roleStore from '../../stores/roleStore'
import SortDepartmentModal from './SortDepartmentModal/SortDepartmentModal'
import {
  ACL_ACTION_TYPE,
  MODULE_CODE,
} from '../../constants'
import AccessControlAction from '../../components/AccessControlAction/AccessControlAction'
import useCheckPageAccess from '../../components/AccessPageAction/CheckPageAccess'
import useCheckPageAccessModule from '../../components/AccessPageAction/CheckPageAccessModule'
import AppPageHeader from '../../components/AppPageHeader'
import AppPagination from '../../components/Common/AppPagination'
import { AppPaginationWrapper } from '../../components/Common/AppPaginationStyled'
import { getContactInitialName } from '../Contacts/contactConstants'
import {
  DepartmentActionButton,
  DepartmentActionGroup,
  DepartmentButton,
  DepartmentList,
  DepartmentNav,
  DepartmentPanel,
  DepartmentPanelHeader,
  DepartmentTitle,
  DepartmentTitleWrapper,
  EmptyUserWrapper,
  ManageDepartmentContent,
  ManageDepartmentContentWrapper,
  ManageDepartmentHeader,
  ManageDepartmentLayout,
  UserCardButton,
  UserContactRow,
  UserContactText,
  UserGrid,
  UserMeta,
  UserName,
  UserPosition,
} from './ManageDepartmentPageStyled'

const DepartmentUserCard = memo(({ user }) => {
  const displayName = user?.name_lowercase || user?.name_uppercase || user?.name
  const positionName = user?.position?.name || 'Chưa cập nhật'
  const avatarText = getContactInitialName(user)

  return (
    <UserCardButton>
      <Avatar
        className="card-avatar"
        size={76}
        src={user?.imageId && `${apiUrl}/api/v1/images/${user.imageId}`}>
        {avatarText}
      </Avatar>
      <Tooltip title={displayName}>
        <UserName className="employee-name">
          {displayName || 'Chưa cập nhật'}
        </UserName>
      </Tooltip>
      <Tooltip title={positionName}>
        <UserPosition>{positionName}</UserPosition>
      </Tooltip>
      <UserMeta>
        <UserContactRow>
          <MailOutlined />
          <Tooltip title={user?.email}>
            <UserContactText>
              {user?.email || 'Chưa cập nhật'}
            </UserContactText>
          </Tooltip>
        </UserContactRow>
        <UserContactRow>
          <PhoneOutlined />
          <Tooltip title={user?.phone}>
            <UserContactText>
              {user?.phone || 'Chưa cập nhật'}
            </UserContactText>
          </Tooltip>
        </UserContactRow>
      </UserMeta>
    </UserCardButton>
  )
})

const ManageDepartmentPage = manageDepartmentPageProps => {
  const {
    departmentStore,
    userStore,
    loadingAnimationStore,
    aclStore,
    history,
    authenticationStore,
    moduleStore,
  } = manageDepartmentPageProps

  const { departmentList } = departmentStore

  const {
    userOfDepartmentListTotalCount,
    userOfDepartmentListPageIndex,
    userOfDepartmentListPageSize,
  } = userStore
  const { aclActionsByUser } = aclStore
  const { isAccountAdmin } = authenticationStore
  const { moduleList } = moduleStore

  const [userOfDepartmentData, setUserOfDepartmentData] = useState([])
  const [currentDepartment, setCurrentDepartment] = useState()
  const [checkUpdate, setCheckUpdate] = useState(false)
  const [checkDel, setCheckDel] = useState(false)

  //create/edit department modal
  const [
    isVisibleCreateDepartmentModal,
    setIsVisibleCreateDepartmentModal,
  ] = useState(false)
  const [mode, setMode] = useState(null)

  //add user
  const [isVisibleAddUser, setIsVisibleAddUser] = useState(false)
  //sort department
  const [
    isVisibleSortDepartmentModal,
    setIsVisibleSortDepartmentModal,
  ] = useState(false)
  // delete department confirm popover
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false)

  // Check page access module
  useCheckPageAccessModule({
    history,
    moduleList,
    actionCode: MODULE_CODE.quan_tri,
    childActionCode: MODULE_CODE.phong_ban,
  })

  // Check page access
  useCheckPageAccess({
    history,
    aclActionsByUser,
    isAccountAdmin,
    actionCode: ACL_ACTION_TYPE.menu__QUAN_LY_PHONG_BAN,
  })

  const showEditDepartmentModal = useCallback(() => {
    setIsVisibleCreateDepartmentModal(true)
    setMode(MODAL_MODE.UPDATE)
  }, [])

  const loadUserList = useCallback(async () => {
    userStore.setFilter('userOfDepartmentListHasAdmin', true)
    let response = await userStore.getUserListByDepartment()
    setUserOfDepartmentData(response.data)
  }, [userStore])

  const onChangeTabDepartment = useCallback(async key => {
    if (currentDepartment?.code === key) return
    try {
      loadingAnimationStore.setTableLoading(true)

      userStore.setFilter('userOfDepartmentListByDepartmentCode', key)
      userStore.setFilter('userOfDepartmentListPageIndex', 0)

      await loadUserList()

      setCurrentDepartment(find(departmentList, { code: key }))
    } catch (e) {
      message.error(e.vi || 'Đã có lỗi xảy ra')
    } finally {
      loadingAnimationStore.setTableLoading(false)
    }
  }, [
    currentDepartment?.code,
    departmentList,
    loadUserList,
    loadingAnimationStore,
    userStore,
  ])

  const onChangePagination = useCallback(async (pageIndex, pageSize) => {
    try {
      loadingAnimationStore.setTableLoading(true)
      userStore.setFilter('userOfDepartmentListHasAdmin', true)
      userStore.setFilter('userOfDepartmentListPageIndex', pageIndex - 1)
      userStore.setFilter('userOfDepartmentListPageSize', pageSize)

      let response = await userStore.getUserListByDepartment()
      setUserOfDepartmentData(response.data)
    } catch (e) {
      message.warning('Lỗi lấy thông tin người dùng trong phòng ban!')
    } finally {
      loadingAnimationStore.setTableLoading(false)
    }
  }, [loadingAnimationStore, userStore])

  const handleDelete = useCallback(async () => {
    if (userOfDepartmentListTotalCount === 0) {
      try {
        loadingAnimationStore.showSpinner(true)

        await departmentStore.delete(currentDepartment?.code)

        await departmentStore.getAllDepartmentList(
          authenticationStore.currentCompanyCode()
        )
        setCheckDel(true)
      } catch (e) {
        message.error(e.vi || 'Đã có sự cố xảy ra')
      } finally {
        loadingAnimationStore.showSpinner(false)
      }
    } else {
      message.warning('Không thể xóa phòng ban đã có nhân viên')
    }
  }, [
    authenticationStore,
    currentDepartment?.code,
    departmentStore,
    loadingAnimationStore,
    userOfDepartmentListTotalCount,
  ])

  const showCreateDepartmentModal = useCallback(() => {
    setIsVisibleCreateDepartmentModal(true)
    setMode(MODAL_MODE.CREATE)
  }, [])

  const handleCloseCreateDepartmentModal = useCallback(() => {
    setIsVisibleCreateDepartmentModal(false)
    setMode(null)
  }, [])

  const showAddUser = useCallback(() => {
    setIsVisibleAddUser(true)
  }, [])

  const handleCancelAddUser = useCallback(() => {
    setIsVisibleAddUser(false)
  }, [])

  const onRefreshListUser = useCallback(() => {
    loadUserList()
  }, [loadUserList])

  const showSortDepartmentModal = useCallback(() => {
    setIsVisibleSortDepartmentModal(true)
  }, [])

  const handleCancelSortDepartmentModal = useCallback(() => {
    setIsVisibleSortDepartmentModal(false)
  }, [])

  const onRefreshListDepartment = useCallback(async () => {
    loadingAnimationStore.showSpinner(true)
    await departmentStore.getAllDepartmentList(
      authenticationStore.currentCompanyCode()
    )
    loadingAnimationStore.showSpinner(false)
  }, [authenticationStore, departmentStore, loadingAnimationStore])

  useEffect(() => {
    if (authenticationStore.currentUser) {
      loadingAnimationStore.showSpinner(true)

      Promise.all([
        departmentStore.getAllDepartmentList(
          authenticationStore.currentCompanyCode()
        ),
        //for create user account
        positionStore.getPositionList(),
        roleStore.getRoles(),
      ])
        .then(async responses => {
          let firstDept = responses[0].data[0]
          if (!firstDept) {
            setCurrentDepartment(undefined)
            setUserOfDepartmentData([])
            return
          }
          setCurrentDepartment(firstDept)

          userStore.setFilter(
            'userOfDepartmentListByDepartmentCode',
            firstDept.code
          )
          userStore.setFilter('userOfDepartmentListHasAdmin', true)

          let response = await userStore.getUserListByDepartment()
          setUserOfDepartmentData(response.data)
        })
        .catch(e => {
          message.warning('Lỗi lấy thông tin phòng ban!').then()
        })
        .finally(() => {
          loadingAnimationStore.showSpinner(false)
        })
    }

    return () => {
      departmentStore.clearDepartmentList()
    }
  }, [authenticationStore.currentUser])

  useEffect(() => {
    Promise.all([
      departmentStore.getAllDepartmentList(
        authenticationStore.currentCompanyCode()
      ),
      //for create user account
      positionStore.getPositionList(),
      roleStore.getRoles(),
    ])
      .then(async responses => {
        let firstDept = responses[0].data[0]
        if (!firstDept) {
          setCurrentDepartment(undefined)
          setUserOfDepartmentData([])
          setCheckDel(false)
          return
        }
        setCurrentDepartment(firstDept)

        userStore.setFilter(
          'userOfDepartmentListByDepartmentCode',
          firstDept.code
        )
        userStore.setFilter('userOfDepartmentListHasAdmin', true)
        let response = await userStore.getUserListByDepartment()
        setUserOfDepartmentData(response.data)
        setCheckDel(false)
      })
      .catch(e => {
        message.warning('Lỗi lấy thông tin phòng ban!').then()
      })
      .finally(() => {
        loadingAnimationStore.showSpinner(false)
      })
  }, [checkUpdate || checkDel])

  return (
    <>
      <DashboardLayout>
        <Helmet>
          <title>Quản lý phòng ban</title>
        </Helmet>
        <ManageDepartmentHeader>
          <AppPageHeader
            title={'Quản lý phòng ban'}
            addon={
              <Space direction={'horizontal'}>
                <AccessControlAction
                  aclActionType={ACL_ACTION_TYPE.sort__QUAN_LY_PHONG_BAN}>
                  <Button
                    className="custom-ant-button icon-btn"
                    icon={<OrderedListOutlined />}
                    onClick={showSortDepartmentModal}>
                    Sắp xếp
                  </Button>
                </AccessControlAction>
                <AccessControlAction
                  aclActionType={ACL_ACTION_TYPE.create__QUAN_LY_PHONG_BAN}>
                  <Button
                    className="custom-ant-button icon-btn"
                    icon={<PlusOutlined />}
                    onClick={showCreateDepartmentModal}
                    type="primary">
                    Tạo phòng ban
                  </Button>
                </AccessControlAction>
              </Space>
            }
          />
        </ManageDepartmentHeader>
        <ManageDepartmentContentWrapper>
          <ManageDepartmentContent>
            <ManageDepartmentLayout>
              <DepartmentNav>
                <DepartmentList>
                  {departmentList.map(department => (
                    <Tooltip
                      title={department.name}
                      key={department.code}
                      placement="right">
                      <DepartmentButton
                        htmlType="button"
                        className={
                          currentDepartment?.code === department.code
                            ? 'active'
                            : ''
                        }
                        onClick={() => onChangeTabDepartment(department.code)}>
                        <span>{department.name}</span>
                      </DepartmentButton>
                    </Tooltip>
                  ))}
                </DepartmentList>
              </DepartmentNav>

              <DepartmentPanel>
                {currentDepartment ? (
                  <>
                    <DepartmentPanelHeader>
                      <DepartmentTitleWrapper>
                        <DepartmentTitle>
                          {currentDepartment.name}
                        </DepartmentTitle>
                        {userOfDepartmentListTotalCount !== undefined &&
                          userOfDepartmentListTotalCount !== null && (
                            <span className="count-badge">
                              {userOfDepartmentListTotalCount} nhân sự
                            </span>
                          )}
                      </DepartmentTitleWrapper>
                      <DepartmentActionGroup>
                        <AccessControlAction
                          aclActionType={
                            ACL_ACTION_TYPE.edit__QUAN_LY_PHONG_BAN
                          }>
                          <Tooltip title={'Chỉnh sửa phòng ban'}>
                            <DepartmentActionButton
                              htmlType="button"
                              aria-label={'Chỉnh sửa phòng ban'}
                              onClick={showEditDepartmentModal}>
                              <EditOutlined />
                            </DepartmentActionButton>
                          </Tooltip>
                        </AccessControlAction>
                        <AccessControlAction
                          aclActionType={
                            ACL_ACTION_TYPE.create_employee__QUAN_LY_PHONG_BAN
                          }>
                          <Tooltip title={'Thêm nhân viên'}>
                            <DepartmentActionButton
                              htmlType="button"
                              aria-label={'Thêm nhân viên'}
                              onClick={showAddUser}>
                              <UserAddOutlined />
                            </DepartmentActionButton>
                          </Tooltip>
                        </AccessControlAction>
                        <AccessControlAction
                          aclActionType={
                            ACL_ACTION_TYPE.delete__QUAN_LY_PHONG_BAN
                          }>
                          <Tooltip
                            title={deleteConfirmVisible ? '' : 'Xóa phòng ban'}>
                            <Popconfirm
                              visible={deleteConfirmVisible}
                              onVisibleChange={setDeleteConfirmVisible}
                              onConfirm={() => {
                                setDeleteConfirmVisible(false)
                                handleDelete()
                              }}
                              onCancel={() => setDeleteConfirmVisible(false)}
                              okText={'Đồng ý'}
                              cancelText={'Không'}
                              okType={'danger'}
                              placement="topRight"
                              overlayClassName="department-delete-popconfirm"
                              icon={
                                <QuestionCircleOutlined
                                  style={{ color: '#ef4444' }}
                                />
                              }
                              title={'Bạn có muốn xóa phòng ban này không?'}>
                              <DepartmentActionButton
                                danger
                                htmlType="button"
                                aria-label={'Xóa phòng ban'}>
                                <DeleteOutlined />
                              </DepartmentActionButton>
                            </Popconfirm>
                          </Tooltip>
                        </AccessControlAction>
                      </DepartmentActionGroup>
                    </DepartmentPanelHeader>

                    <Spin spinning={loadingAnimationStore.tableLoading}>
                      {userOfDepartmentData.length === 0 ? (
                        <EmptyUserWrapper>
                          <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={
                              'Không có người dùng trong phòng ban này'
                            }
                          />
                        </EmptyUserWrapper>
                      ) : (
                        <>
                          <UserGrid>
                            {userOfDepartmentData.map(user => (
                              <DepartmentUserCard
                                key={user.code || user.username}
                                user={user}
                              />
                            ))}
                          </UserGrid>
                          <AppPaginationWrapper>
                            <AppPagination
                              onChange={onChangePagination}
                              total={userOfDepartmentListTotalCount}
                              pageSize={userOfDepartmentListPageSize}
                              current={userOfDepartmentListPageIndex + 1}
                            />
                          </AppPaginationWrapper>
                        </>
                      )}
                    </Spin>
                  </>
                ) : (
                  <EmptyUserWrapper>
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={'Chưa có phòng ban nào'}
                    />
                  </EmptyUserWrapper>
                )}
              </DepartmentPanel>
            </ManageDepartmentLayout>
          </ManageDepartmentContent>
        </ManageDepartmentContentWrapper>
      </DashboardLayout>

      <CreateDepartmentModal
        setCheckUpdate={setCheckUpdate}
        isVisibleCreateDepartmentModal={isVisibleCreateDepartmentModal}
        mode={mode}
        handleCloseCreateDepartmentModal={handleCloseCreateDepartmentModal}
        currentDepartment={currentDepartment}
      />

      <AddUserForm
        currentDepartment={currentDepartment}
        modalVisible={isVisibleAddUser}
        onCancel={handleCancelAddUser}
        onRefreshListUser={onRefreshListUser}
      />

      <SortDepartmentModal
        departmentList={departmentList}
        isVisibleSortDepartmentModal={isVisibleSortDepartmentModal}
        onCancel={handleCancelSortDepartmentModal}
        handleCancelSortDepartmentModal={handleCancelSortDepartmentModal}
        onRefreshListDepartment={onRefreshListDepartment}
      />
    </>
  )
}

ManageDepartmentPage.propTypes = {}

export default memo(
  inject(
    'departmentStore',
    'userStore',
    'loadingAnimationStore',
    'aclStore',
    'authenticationStore',
    'moduleStore'
  )(observer(ManageDepartmentPage))
)
